import React, { useMemo, useState } from 'react'

// 獨立可用：純 React（JavaScript）元件
// 需求：移除「已答/未答」選項，只保留「選中 / 未選中」
// 功能：可新增多筆規則 → 轉換成 visibleIfMap 與 routes（題號/選項自動 -1）

export default function QuestionnaireSkipConverter() {
  // ----- 狀態 -----
  const [rows, setRows] = useState(() => [newEmptyRow()])
  const [result, setResult] = useState({
    json: '{}',
    human: '（轉換後會顯示在這裡）',
  })
  const [counter, setCounter] = useState(1)

  // ----- 工具函式 -----
  function newEmptyRow(preset) {
    return {
      id: cryptoRandomId(),
      if_q: preset?.if_q ?? '',
      q_type: preset?.q_type ?? '單選', // 單選 / 複選
      state: preset?.state ?? '選中', // 選中 / 未選中（已移除 已答/未答）
      option: preset?.option ?? '',
      then_action: preset?.then_action ?? '顯示', // 顯示 / 隱藏 / 跳轉至
      then_q: preset?.then_q ?? '',
    }
  }

  function cryptoRandomId() {
    if (window.crypto?.getRandomValues) {
      const buf = new Uint32Array(2)
      window.crypto.getRandomValues(buf)
      return `r${buf[0].toString(36)}${buf[1].toString(36)}`
    }
    return `r${Math.random().toString(36).slice(2)}`
  }

  function clampInt(val) {
    if (val === '' || val == null) return ''
    const n = Number(val)
    if (!Number.isFinite(n)) return ''
    return String(Math.max(1, Math.floor(n)))
  }

  // 條件字串（-1 規則內建處理）
  function buildCondition(S, qType, state, option) {
    const Snum = Number(S)
    const Onum = Number(option)
    if (!Number.isFinite(Snum)) throw new Error('題目編號 S 必填且須為數字')
    if (!Number.isFinite(Onum))
      throw new Error('狀態為『選中/未選中』時，選項必填')

    const S_ = Snum - 1
    const idx = Onum - 1
    const qRef = `{q${S_}}`

    if (qType === '單選') {
      if (state === '選中') return `${qRef} = ${idx}`
      if (state === '未選中') return `${qRef} != ${idx}`
    } else {
      // 複選
      if (state === '選中') return `${qRef} anyof [${idx}]`
      if (state === '未選中') return `${qRef} noneof [${idx}]`
    }
    throw new Error('未知的題目類型或情況')
  }

  function negate(cond) {
    if (cond.includes(' = ')) return cond.replace(' = ', ' != ')
    if (cond.includes(' != ')) return cond.replace(' != ', ' = ')
    if (cond.includes(' anyof ')) return cond.replace(' anyof ', ' noneof ')
    if (cond.includes(' noneof ')) return cond.replace(' noneof ', ' anyof ')
    return `not (${cond})`
  }

  function convertRules(inputRows) {
    const visibleIfMap = {}
    const routes = []

    function pushVisible(qHuman, cond) {
      const key = `q${qHuman - 1}` // 內部再 -1
      if (!visibleIfMap[key]) visibleIfMap[key] = []
      visibleIfMap[key].push(cond)
    }

    for (const r of inputRows) {
      const S = Number(r.if_q)
      const T = Number(r.then_q)
      if (!Number.isFinite(S) || !Number.isFinite(T)) {
        throw new Error('題目編號不得為空，且需為數字')
      }

      const cond = buildCondition(S, r.q_type, r.state, r.option)
      const S_ = S - 1
      const T_ = T - 1

      if (r.then_action === '顯示') {
        pushVisible(T, cond)
      } else if (r.then_action === '隱藏') {
        pushVisible(T, negate(cond))
      } else if (r.then_action === '跳轉至') {
        if (T_ <= S_) throw new Error(`跳轉至需要 T > S（收到 S=${S}, T=${T}）`)
        for (let q = S_ + 1; q <= T_ - 1; q++) {
          const qHuman = q + 1
          pushVisible(qHuman, negate(cond))
        }
        routes.push({ from: `q${S_}`, to: `q${T_}`, when: cond })
      }
    }

    return { visibleIfMap, routes }
  }

  function toHuman({ visibleIfMap, routes }) {
    const keys = Object.keys(visibleIfMap).sort(
      (a, b) => Number(a.slice(1)) - Number(b.slice(1))
    )
    const lines = []
    if (keys.length) {
      lines.push('【visibleIfMap】')
      for (const k of keys) {
        const arr = visibleIfMap[k]
        lines.push(
          `${k}: ${arr.length > 1 ? '(' + arr.join(') and (') + ')' : arr[0]}`
        )
      }
    } else {
      lines.push('【visibleIfMap】(空)')
    }
    lines.push('')
    if (routes.length) {
      lines.push('【routes】')
      for (const r of routes) lines.push(`${r.from} → ${r.to} when ${r.when}`)
    } else {
      lines.push('【routes】(空)')
    }
    return lines.join('\n')
  }

  // ----- 事件處理 -----
  function addRow(preset) {
    setRows((prev) => [...prev, newEmptyRow(preset)])
    setCounter((n) => n + 1)
  }
  function insertExamples() {
    addRow({
      if_q: '11',
      q_type: '複選',
      state: '選中',
      option: '2',
      then_action: '顯示',
      then_q: '12',
    })
    addRow({
      if_q: '18',
      q_type: '單選',
      state: '選中',
      option: '1',
      then_action: '顯示',
      then_q: '19',
    })
    addRow({
      if_q: '3',
      q_type: '單選',
      state: '選中',
      option: '2',
      then_action: '跳轉至',
      then_q: '6',
    })
  }
  function clearAll() {
    setRows([newEmptyRow()])
    setResult({ json: '{}', human: '（轉換後會顯示在這裡）' })
  }
  function convert() {
    try {
      // 基礎驗證 + 整理
      const cleaned = rows
        .map((r) => ({
          ...r,
          if_q: String(r.if_q).trim(),
          then_q: String(r.then_q).trim(),
          option: String(r.option).trim(),
        }))
        .filter((r) => r.if_q !== '' && r.then_q !== '')

      if (!cleaned.length) throw new Error('請先新增並填寫至少 1 筆規則')

      // 狀態為選中/未選中時，option 必填
      for (const r of cleaned) {
        if (['選中', '未選中'].includes(r.state) && r.option === '') {
          throw new Error('當情況為『選中/未選中』時，『選項』必填（數字）')
        }
      }

      const out = convertRules(cleaned)
      setResult({ json: JSON.stringify(out, null, 2), human: toHuman(out) })
    } catch (e) {
      setResult({ json: '{}', human: '⚠️ ' + (e?.message || String(e)) })
    }
  }
  async function copyJson() {
    try {
      await navigator.clipboard.writeText(result.json)
      alert('已複製 JSON！')
    } catch (e) {
      alert('無法複製：' + (e?.message || String(e)))
    }
  }

  function moveRow(id, dir) {
    setRows((prev) => {
      const idx = prev.findIndex((r) => r.id === id)
      if (idx < 0) return prev
      const next = [...prev]
      const swapWith = dir === -1 ? idx - 1 : idx + 1
      if (swapWith < 0 || swapWith >= next.length) return prev
      ;[next[idx], next[swapWith]] = [next[swapWith], next[idx]]
      return next
    })
  }
  function deleteRow(id) {
    setRows((prev) =>
      prev.length <= 1 ? [newEmptyRow()] : prev.filter((r) => r.id !== id)
    )
  }

  function updateRow(id, patch) {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)))
  }

  // 每列的即時預覽
  function renderPreview(row) {
    try {
      const c = buildCondition(row.if_q, row.q_type, row.state, row.option)
      const S_ = Number(row.if_q) - 1
      const T_ = Number(row.then_q) - 1
      let hint = `條件：${c}　（S'=${isFinite(S_) ? S_ : '?'}, T'=${
        isFinite(T_) ? T_ : '?'
      }`
      if (row.option !== '') hint += `, Idx=${Number(row.option) - 1}`
      hint += ')'
      if (row.then_action === '顯示')
        hint += ` → 對 q${isFinite(T_) ? T_ : '?'} 設 visibleIf`
      else if (row.then_action === '隱藏')
        hint += ` → 對 q${isFinite(T_) ? T_ : '?'} 設 visibleIf = NOT(條件)`
      else
        hint += ` → 隱藏 q${isFinite(S_) ? S_ + 1 : '?'}..q${
          isFinite(T_) ? T_ - 1 : '?'
        }（當條件成立）`
      return { text: hint, ok: true }
    } catch (e) {
      return { text: e?.message || String(e), ok: false }
    }
  }

  return (
    <div className="wrap">
      <h1 className="title">問卷跳題轉換器（React 元件 / JS）</h1>
      <p className="sub">
        使用數值與下拉選單輸入規則，按「轉換」即可得到{' '}
        <span className="pill">visibleIfMap</span> 與
        <span className="pill"> routes</span>（題號/選項自動 -1）。
        <br />
        <b>已移除「已答 / 未答」選項</b>，僅保留「選中 / 未選中」。
      </p>

      <div className="toolbar">
        <button className="btn btn-accent" onClick={() => addRow()}>
          ＋ 新增規則
        </button>
        <button className="btn" onClick={insertExamples}>
          插入範例（3 筆）
        </button>
        <button className="btn btn-green" onClick={convert}>
          轉換
        </button>
        <button className="btn" onClick={copyJson}>
          複製 JSON
        </button>
        <button className="btn btn-danger" onClick={clearAll}>
          清空
        </button>
      </div>

      {rows.map((row, i) => {
        const p = renderPreview(row)
        return (
          <div key={row.id} className="card rule">
            <div className="rule-grid">
              <div className="field full">
                <span className="pill">若</span>
              </div>

              <div className="field span-2">
                <label>1. 題目編號（數字）</label>
                <input
                  type="number"
                  min={1}
                  step={1}
                  placeholder="例如：11"
                  value={row.if_q}
                  onChange={(e) =>
                    updateRow(row.id, { if_q: clampInt(e.target.value) })
                  }
                />
              </div>

              <div className="field span-2">
                <label>2. 題目類型</label>
                <select
                  value={row.q_type}
                  onChange={(e) =>
                    updateRow(row.id, { q_type: e.target.value })
                  }
                >
                  <option value="單選">單選</option>
                  <option value="複選">複選</option>
                </select>
              </div>

              <div className="field span-3">
                <label>3. 情況</label>
                <select
                  value={row.state}
                  onChange={(e) => updateRow(row.id, { state: e.target.value })}
                >
                  <option value="選中">選中</option>
                  <option value="未選中">未選中</option>
                </select>
              </div>

              <div className="field span-2">
                <label>4. 選項（數字）</label>
                <input
                  type="number"
                  min={1}
                  step={1}
                  placeholder="例如：2"
                  value={row.option}
                  onChange={(e) =>
                    updateRow(row.id, { option: clampInt(e.target.value) })
                  }
                />
                <div className="hint">情況為「選中/未選中」時必填</div>
              </div>

              <div className="field full">
                <span className="pill">則</span>
              </div>

              <div className="field span-3">
                <label>5. 動作</label>
                <select
                  value={row.then_action}
                  onChange={(e) =>
                    updateRow(row.id, { then_action: e.target.value })
                  }
                >
                  <option value="顯示">顯示</option>
                  <option value="隱藏">隱藏</option>
                  <option value="跳轉至">跳轉至</option>
                </select>
              </div>

              <div className="field span-3">
                <label>6. 題目編號（數字）</label>
                <input
                  type="number"
                  min={1}
                  step={1}
                  placeholder="例如：12"
                  value={row.then_q}
                  onChange={(e) =>
                    updateRow(row.id, { then_q: clampInt(e.target.value) })
                  }
                />
              </div>

              <div className="row-actions span-6">
                <button className="btn" onClick={() => moveRow(row.id, -1)}>
                  上移
                </button>
                <button className="btn" onClick={() => moveRow(row.id, +1)}>
                  下移
                </button>
                <button
                  className="btn btn-danger"
                  onClick={() => deleteRow(row.id)}
                >
                  刪除
                </button>
                <div className="num">#{i + 1}</div>
              </div>

              <div className="hint span-6">
                小提醒：<b>跳轉至</b> 需滿足 T &gt; S；轉換時會隱藏 S’+1 ..
                T’-1。
              </div>
            </div>

            <div className="sep" />
            <div className={'hint mono live-preview ' + (p.ok ? 'ok' : 'err')}>
              {p.text}
            </div>
          </div>
        )
      })}

      <div className="card">
        <div className="result-header">
          <div className="result-title">轉換結果</div>
          <div className="pill">
            合併策略：同題目多條 visibleIf 預設 AND（全部成立才顯示）
          </div>
        </div>
        <div className="two-col">
          <div>
            <div className="sec-title">可讀摘要</div>
            <pre>
              <span className="mono">{result.human}</span>
            </pre>
          </div>
          <div>
            <div className="sec-title">JSON 結果</div>
            <pre>
              <span className="mono">{result.json}</span>
            </pre>
          </div>
        </div>
        <div className="hint">
          備註：「跳轉至」等價於在條件成立時，批次「隱藏」 S’+1 .. T’-1
          的所有題；T’本身通常不需設條件。
        </div>
      </div>

      <style jsx>{`
        :root {
          --bg: #0b0f14;
          --card: #121821;
          --muted: #9fb3c8;
          --fg: #e7eef7;
          --accent: #4da3ff;
          --accent2: #63d297;
          --danger: #ff6b6b;
        }
        .wrap {
          max-width: 1100px;
          margin: 40px auto;
          padding: 0 16px;
          color: var(--fg);
          font: 15px/1.5 ui-sans-serif, system-ui, -apple-system, Segoe UI,
            Roboto;
        }
        .title {
          font-size: 24px;
          font-weight: 700;
          margin: 0 0 12px;
        }
        .sub {
          color: var(--muted);
          margin: 0 0 24px;
        }
        .toolbar {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
          margin-bottom: 12px;
        }
        .btn {
          border: 0;
          background: #18314f;
          color: #cfe5ff;
          padding: 10px 14px;
          border-radius: 12px;
          cursor: pointer;
        }
        .btn:hover {
          filter: brightness(1.1);
        }
        .btn-accent {
          background: linear-gradient(90deg, #3e8df6, #4da3ff);
        }
        .btn-green {
          background: linear-gradient(90deg, #36bf87, #63d297);
        }
        .btn-danger {
          background: #3f1d24;
          color: #ffd6de;
        }
        .pill {
          display: inline-block;
          padding: 2px 8px;
          border-radius: 999px;
          background: #132133;
          color: #b9d7ff;
          font-size: 12px;
          border: 1px solid #1b2e48;
          margin: 0 6px;
        }
        .card {
          background: var(--card);
          border: 1px solid #1b2533;
          border-radius: 16px;
          box-shadow: 0 6px 24px rgba(0, 0, 0, 0.25);
          padding: 16px;
          margin-bottom: 16px;
        }
        .rule-grid {
          display: grid;
          grid-template-columns: repeat(12, 1fr);
          gap: 10px;
          align-items: end;
        }
        .field {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }
        .field.full {
          grid-column: 1/-1;
        }
        .field.span-2 {
          grid-column: span 2;
        }
        .field.span-3 {
          grid-column: span 3;
        }
        .field.span-6 {
          grid-column: span 6;
        }
        label {
          color: #b8c7d8;
          font-size: 12px;
        }
        input[type='number'],
        select {
          background: #0d131c;
          border: 1px solid #1e2a3a;
          color: var(--fg);
          padding: 10px 12px;
          border-radius: 10px;
          outline: none;
        }
        input[type='number']:focus,
        select:focus {
          border-color: var(--accent);
        }
        .row-actions {
          display: flex;
          gap: 8px;
          align-items: center;
        }
        .row-actions .num {
          opacity: 0.7;
          margin-left: auto;
        }
        .hint {
          color: #9bb0c7;
          font-size: 12px;
          margin-top: 6px;
        }
        .mono {
          font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
        }
        pre {
          background: #0d131c;
          border: 1px solid #1e2a3a;
          border-radius: 14px;
          padding: 14px;
          overflow: auto;
          white-space: pre-wrap;
        }
        .two-col {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
        }
        @media (max-width: 980px) {
          .two-col {
            grid-template-columns: 1fr;
          }
        }
        .sep {
          height: 1px;
          background: #1b2533;
          margin: 12px 0;
        }
        .result-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 8px;
        }
        .result-title {
          font-weight: 700;
        }
        .sec-title {
          font-weight: 600;
          margin-bottom: 6px;
        }
        .live-preview.ok {
          color: #cfe5ff;
        }
        .live-preview.err {
          color: var(--danger);
        }
      `}</style>
    </div>
  )
}
